import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import NewsCard from '@/components/feature/NewsCard';
import EmptyState from '@/components/feature/EmptyState';
import { newsList, newsCategories } from '@/mocks/news';

const PAGE_SIZE = 6;

export default function News() {
  const [category, setCategory] = useState('全部');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [category]);

  const sorted = useMemo(() => [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)), []);
  const filtered = useMemo(
    () => (category === '全部' ? sorted : sorted.filter((n) => n.category === category)),
    [category, sorted],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="w-full bg-background-50">
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-screen mx-auto">
          <nav className="text-xs md:text-sm text-foreground-600 mb-4 flex items-center gap-1.5">
            <Link to="/" className="hover:text-primary-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">电影资讯</span>
          </nav>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-black text-foreground-950 mb-2">最新电影资讯</h1>
            <p className="text-sm text-foreground-600 max-w-3xl">
              汇集影评、业内动态、奖项揭晓、预告片与票房数据，帮你第一时间掌握电影行业的最新脉搏。
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {newsCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  category === cat
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 border border-background-300/60 text-foreground-700 hover:text-primary-400'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {pageItems.length === 0 ? (
            <div className="bg-background-100 border border-background-300/50 rounded-lg">
              <EmptyState
                icon="ri-newspaper-line"
                title="暂无最新资讯"
                description="这个分类下还没有内容，换个分类看看吧。"
                actionLabel="查看全部资讯"
                onAction={() => setCategory('全部')}
              />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
                {pageItems.map((article) => (
                  <NewsCard key={article.id} article={article} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-9 h-9 rounded-md bg-background-100 border border-background-300/60 text-foreground-800 flex items-center justify-center disabled:opacity-40 hover:border-primary-500 transition-colors cursor-pointer"
                    aria-label="上一页"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={`w-9 h-9 rounded-md text-sm flex items-center justify-center transition-colors cursor-pointer ${
                        page === i + 1
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 border border-background-300/60 text-foreground-800 hover:border-primary-500'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-9 h-9 rounded-md bg-background-100 border border-background-300/60 text-foreground-800 flex items-center justify-center disabled:opacity-40 hover:border-primary-500 transition-colors cursor-pointer"
                    aria-label="下一页"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}